/* Planned versus performed, for one assigned day.
 *
 * `EVERFIT-MAP.md` §2b: their client page carries an **Assignment ⇄ History toggle** — the same
 * calendar, one switch, planned or performed. The map's verdict is DISCARD the model, ADD two
 * things, and this is one of them; it also notes that a shipping product answers the V7 review
 * loop's own question (`docs/V7-REVIEW-LOOP-SPEC.md`) with a toggle rather than a separate screen.
 *
 * PURE on purpose — `training.test.ts` executes it. The Swift harness cannot reach TypeScript.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════
 * THE FOUR MEASUREMENTS THAT DECIDED THIS FILE. All executed through RLS as the coach on
 * 2026-08-28, over his two real clients, before a line of the screen was written.
 *
 * 1. **THE PLANNED AND PERFORMED SIDES SHARE ONE VOCABULARY, AND IT IS THE LIBRARY ID.**
 *    `workout_session_exercises.coach_exercise_id` is populated on **332 of 1341** rows and
 *    **all 332 resolve to `coach_exercises`** — the library — while **ZERO** resolve to
 *    `coach_workout_exercises`. That is `CLAUDE.md`'s instance fourteen confirmed live and in
 *    the right direction: the column wants the LIBRARY id and now holds it.
 *
 * 2. ⚠️ **A LIBRARY ID IS NOT UNIQUE WITHIN A DAY, SO IT CANNOT PAIR ON ITS OWN.** Of 141
 *    (client, date, library-exercise) triples, **101 appear once and 40 appear twice** — 28%
 *    ambiguous. The athlete side has the same shape: V7-1 measured 32.6% of live day-exercise
 *    pairs carrying two or more appearances. A warm-up set of pull-ups and a main set of
 *    pull-ups are the same library row.
 *
 * 3. **POSITION PAIRS THEM, AND IT WAS VERIFIED ON DATA THE ATHLETE ACTUALLY LOGGED.** Flatten
 *    the plan by `(section.position, exercise.position)` and the performed side by `position`,
 *    and the two lists correspond exactly. **20 of 20 on 2026-08-11 — a day with EIGHT
 *    duplicated library ids, every one resolved.**
 *    ⚠️ It was checked on a genuinely logged day ON PURPOSE. The obvious day to test is
 *    2026-08-24, which also matches 23 of 23 — and it is part of the week
 *    `tools/v7-relog-week/01-relog.sql` RECONSTRUCTED, so agreeing there would have measured
 *    that script rather than the app. A fixture that models nobody, one layer up.
 *
 * 3b. ⚠️ **THE TWO SIDES COUNT FROM DIFFERENT NUMBERS, SO THE PAIRING COMPARES ORDER AND NEVER
 *    THE VALUE.** Measured: every coach-side position is **0-based** (`coach_workout_sections`
 *    0..10, `coach_workout_exercises` 0..11, `coach_workout_sets` 0..9) while
 *    `workout_session_exercises.position` is **1-based** (1..23), as is `set_logs.set_number`.
 *    That is `CLAUDE.md`'s recorded *"one ordinal, two bases"* — the defect that made
 *    `tools/v7-relog-week` fail its rehearsal — sitting between exactly the two tables this file
 *    joins. `listsCorrespond` sorts both sides and walks them in step, so it is immune; a
 *    "simplification" to `planned.find(p => p.ordinal === performed.ordinal)` would compile,
 *    render every row, and pair nothing correctly. Pinned by a test.
 *
 * 3c. ⚠️ **`workout_session_exercises.position` IS NOT UNIQUE WITHIN A SESSION — 150 duplicated
 *    (session, position) pairs across 50 sessions — AND NONE OF THEM CAN REACH THIS FILE.**
 *    Measured: of those 50 sessions, **ZERO carry a `coach_exercise_id`**, so they are engine-era
 *    work, and `listsCorrespond` refuses any list whose library id is null. The 8 sessions that
 *    are both coach-linked and hold logged sets have no duplicates. So the ordering is
 *    deterministic **on exactly the sessions this screen pairs** — a bounded claim, not a
 *    property of the column. **Reopen it if the player ever writes a coach-linked session with
 *    two rows at one position**: the sort would then be arbitrary between them and the pairing
 *    would be a coin toss that always looks confident.
 *
 * 4. ⚠️ **"THE DAY'S SESSION" IS NOT ONE ROW, AND PICKING ONE IS LOSSY.** 2026-08-27 alone
 *    carries **sixteen** `workout_sessions` rows for one client, **two** of which hold work —
 *    46 logged sets and 4. So a per-exercise view built on the biggest session silently drops
 *    four sets. That is why this file answers TWO questions with two mechanisms and refuses to
 *    let one stand in for the other:
 *
 *      • **"How much did they do that day?"** — summed across every session on that date, in
 *        `client.ts`, from `set_logs`. Never lossy.
 *      • **"Which prescribed exercise got what?"** — needs ONE ordered list, so it pairs against
 *        the session that MATCHES the plan, and where the two totals disagree it SAYS SO
 *        (`reconcile` below) rather than quietly showing the smaller number.
 * ══════════════════════════════════════════════════════════════════════════════════════════
 */

// ⚠️ THE WEIGHT CONVERSION IS IMPORTED, NOT RESTATED, AND THE FIRST DRAFT OF THIS FILE GOT IT
// WRONG. It carried a private `weight()` helper under a comment claiming the conversion was
// "imported rather than restated so there is one conversion" — a comment asserting the opposite
// of what the code did, which is this repo's own recorded trap and the reason `EVERFIT-MAP.md`
// §20a's per-client units rule is worth having in exactly one place.
import { formatWeight } from "./client";
import { runPhrase } from "./phoneDose";
import type { MediaItem } from "./media";

/** One prescribed set, as the builder stores it in `coach_workout_sets`. */
export type PlannedSet = {
  reps: number | null;
  weightKg: number | null;
  timeSeconds: number | null;
  restSeconds: number | null;
  setType: string | null;
  /** W136 B2 S3 — `coach_workout_sets.distance_m` (metres) / `rounds`; absent = not prescribed. */
  distanceM?: number | null;
  rounds?: number | null;
};

/**
 * One prescribed exercise.
 *
 * ⚠️ THE SETS COME FROM `coach_workout_sets` AND NEVER FROM THE SUMMARY COLUMNS.
 * `coach_workout_exercises` still carries `sets`/`reps_min`/`reps_max`/`hold_seconds_*`, and
 * V6-8 measured that **the builder reads only `coach_workout_sets`** — the per-exercise columns
 * are legacy and appear in no select. Reading them would produce a screen that looks right in
 * the database and disagrees with what the coach actually authored. Measured here too: **699
 * set rows across 323 exercises, and ZERO exercises with no set rows.**
 */
export type PlannedExercise = {
  /** 1-based, flattened by (section.position, exercise.position). */
  ordinal: number;
  libraryExerciseId: string | null;
  name: string | null;
  sectionName: string | null;
  sets: PlannedSet[];
  note: string | null;
  /** W136 B2 S3 — the coach exercise's field is `distance_km` (`tracking_fields`): a run's distance reads in km. */
  distanceKm?: boolean;
};

/** One logged set. */
export type PerformedSet = {
  /** 1-based `set_logs.set_number` — the key a set's clips are filed on (W116). Absent on the pairing fixtures that predate
   *  it, which then simply carry no clip button. */
  setNumber?: number | null;
  reps: number | null;
  weightKg: number | null;
  holdSeconds: number | null;
  /** W136 B2 S3 — `set_logs.distance_m` / `rounds`; absent = not recorded. */
  distanceM?: number | null;
  rounds?: number | null;
  /** RPE as stored (`set_logs.rpe`); the athlete ANSWERS in reps in reserve — see `rirFromRpe`. */
  rpe: number | null;
  /* 🔴 W127 (his word, 2026-10-01: "why there's still the band as field in my training in some exercises? this needs to be
   * gone from everywhere, for ever. and if any band for me then mention which, in the comments/notes of each exercise."):
   * the per-set band label (`set_logs.modifier_label_override`, the phone's chip of 2026-09-06) is no longer read or
   * printed here. The coach writes the band in the exercise's note; the stored labels stay in the database untouched. */
};

/**
 * Reps in reserve from a stored RPE — THE ONE conversion, the same 10 − RPE the phone's rules
 * use (`V6Progression.LoggedSet.rir`). The athlete answers "2 RIR"; the column keeps RPE 8.
 */
export function rirFromRpe(rpe: number | null): number | null {
  return rpe === null ? null : Math.round(10 - rpe);
}

/**
 * The effort line under a logged set: the reps in reserve, or null when none is stored. Kept APART
 * from `describePerformedSet` so the planned and performed columns keep one grammar and compare by
 * eye. W127: the band/rung half is gone — no band label is printed anywhere (his word, 2026-10-01).
 */
export function describeSetEffort(s: PerformedSet): string | null {
  const rir = rirFromRpe(s.rpe);
  return rir === null ? null : `${rir} RIR`;
}

/** One performed exercise, as the player seeded and the athlete filled it. */
export type PerformedExercise = {
  /** 1-based, by `workout_session_exercises.position`. */
  ordinal: number;
  libraryExerciseId: string | null;
  loggedSets: PerformedSet[];
  /**
   * The clips and photos the athlete saved on this exercise (`form_clips`, his 2026-09-05
   * decision). ABSENT means the read did not ask for them (the pairing fixtures predate the
   * column); `[]` means it asked and there are none. Two different answers, kept apart on purpose.
   */
  media?: MediaItem[];
};

/**
 * Choose the session that the plan can be read against.
 *
 * ⚠️ NOT "THE BIGGEST", AND THAT IS THE WHOLE POINT. The tempting rule is the session with the
 * most logged sets, and on a day with sixteen ghost rows it usually picks the right one — but it
 * is a guess with no way to be wrong out loud. This asks the only question that matters: **does
 * this session's ordered list of library exercises equal the plan's?** A session that matches can
 * be paired position by position with no ambiguity; one that does not, cannot, whatever its size.
 *
 * Returns null when nothing matches, which is a real answer and not a failure — the screen then
 * shows the two sides without claiming a pairing.
 */
export function sessionMatchingPlan<S extends { exercises: PerformedExercise[] }>(
  sessions: S[],
  planned: PlannedExercise[],
): S | null {
  for (const s of sessions) {
    if (listsCorrespond(planned, s.exercises)) return s;
  }
  return null;
}

/** Same length, and the same library id at every position. */
function listsCorrespond(planned: PlannedExercise[], performed: PerformedExercise[]): boolean {
  if (planned.length === 0 || planned.length !== performed.length) return false;
  // ⚠️ BOTH SORTS ARE LOAD-BEARING AND A MUTATION SHOWED THE TESTS COULD NOT SEE THEM, BECAUSE
  // every fixture happened to arrive already ordered. Neither query orders its rows: the plan's
  // exercises and sets come back as PostgREST nested embeds with no `.order()`, and PostgREST
  // guarantees no order without one. Removing either sort compares two arbitrary orderings and
  // reports `unpairable` on days that pair perfectly — or worse, pairs the wrong rows.
  const p = [...planned].sort((a, b) => a.ordinal - b.ordinal);
  const f = [...performed].sort((a, b) => a.ordinal - b.ordinal);
  return p.every((row, i) => {
    const other = f[i];
    if (other === undefined) return false;
    // ⚠️ THE `!== null` IS NOT REDUNDANT WITH THE EQUALITY, AND A MUTATION PROVED THE TEST SUITE
    // COULD NOT SEE IT. `null === null` is TRUE, so without this clause two rows that BOTH
    // failed to identify themselves would read as corresponding — and the pairing would then
    // put an unknown prescription beside unknown work and call it a match.
    //
    // Measured: `coach_workout_exercises.exercise_id` is NOT NULL with zero null rows, so the
    // DATABASE cannot currently produce a null on the planned side. It is reachable through the
    // TYPE, which is what matters — `PlannedExercise` is a plain struct any caller can build,
    // and the performed side really is nullable (1009 of 1341 rows carry no library id at all).
    // So this guards the type rather than the data, deliberately, and says which.
    return row.libraryExerciseId !== null && row.libraryExerciseId === other.libraryExerciseId;
  });
}

/** What became of one prescribed exercise. */
export type ExerciseOutcome = "done" | "partial" | "notStarted";

export function exerciseOutcome(planned: PlannedExercise, performed: PerformedExercise | null): ExerciseOutcome {
  const logged = performed?.loggedSets.length ?? 0;
  if (logged === 0) return "notStarted";
  // At or above the prescription is done; a coach does not need "did 4 of 3" flagged as an
  // anomaly, and `client.ts` already refuses to clamp an athlete who did more.
  return logged >= planned.sets.length ? "done" : "partial";
}

/**
 * The day, planned beside performed.
 *
 * ⚠️ PAIRED OR NOT PAIRED — NEVER PARTIALLY PAIRED. V7-1's rule, applied to a join instead of to
 * a column: *a column is returned COMPLETE or EMPTY, never partial*, because an empty one
 * degrades to a true weaker sentence and a partial one to a false confident one. A half-paired
 * exercise list is the false-confident case — every row after the first mismatch would name the
 * wrong prescription, and it would look entirely plausible.
 */
export type DayView =
  | { kind: "paired"; rows: { planned: PlannedExercise; performed: PerformedExercise | null }[] }
  | { kind: "notTrained"; planned: PlannedExercise[] }
  | { kind: "unpairable"; planned: PlannedExercise[]; performedCount: number; reason: string };

export function dayView(planned: PlannedExercise[], performed: PerformedExercise[] | null): DayView {
  if (performed === null || performed.length === 0) {
    return { kind: "notTrained", planned };
  }
  if (!listsCorrespond(planned, performed)) {
    const reason =
      planned.length !== performed.length
        ? `the day was assigned ${planned.length} exercise${planned.length === 1 ? "" : "s"} and ${performed.length} ${performed.length === 1 ? "was" : "were"} recorded`
        : "the recorded exercises are not the ones assigned, in order";
    return { kind: "unpairable", planned, performedCount: performed.length, reason };
  }
  const f = [...performed].sort((a, b) => a.ordinal - b.ordinal);
  return {
    kind: "paired",
    rows: [...planned]
      .sort((a, b) => a.ordinal - b.ordinal)
      .map((row, i) => ({ planned: row, performed: f[i] ?? null })),
  };
}

/**
 * Does the per-exercise breakdown account for everything the day recorded?
 *
 * ⚠️ THE ANSWER IS SOMETIMES NO, AND THAT IS MEASURED RATHER THAN FEARED. On 2026-08-27 the day
 * holds **50** logged sets across two sessions while the session matching the plan holds **46**.
 * A screen showing 46 under a counter that says 50 is a screen the coach cannot trust, and the
 * honest repair is not to reconcile the numbers — it is to say that four sets were logged
 * somewhere this breakdown cannot place.
 */
export function reconcile(dayTotal: number, accountedFor: number): string | null {
  if (dayTotal <= accountedFor) return null;
  const missing = dayTotal - accountedFor;
  return `${missing} more set${missing === 1 ? " was" : "s were"} logged this day outside the workout you assigned.`;
}

/** How a prescribed set reads: "8 × 60 kg", "30 s", "8". */
/** W136 B2 S3 — a set carrying a distance or rounds in §5's words (`runPhrase`): `400 m in 1:45` · `9 km in 1:45:30 @ 20 kg` ·
 *  `7 rounds + 5 reps`; null for every other set (its grammar below is unchanged). */
function typedSet(reps: number | null, seconds: number | null, weight: string | null, distanceM: number | null | undefined,
                  rounds: number | null | undefined, km: boolean): string | null {
  const d = distanceM ?? null, r = rounds ?? null;
  if (d === null && r === null) return null;
  const head = runPhrase(1, d === null ? [] : [d], r === null ? [] : [r], r !== null && reps !== null ? [reps] : [],
                         seconds === null ? [] : [seconds], km) as string;
  return weight === null ? head : `${head} @ ${weight}`;
}

export function describePlannedSet(s: PlannedSet, unitSystem: string | null, km = false): string {
  const typed = typedSet(s.reps, s.timeSeconds, formatWeight(s.weightKg, unitSystem), s.distanceM, s.rounds, km);
  if (typed !== null) return typed;
  const bits: string[] = [];
  if (s.reps !== null) bits.push(`${s.reps}`);
  if (s.timeSeconds !== null) bits.push(`${s.timeSeconds}s`);
  const w = formatWeight(s.weightKg, unitSystem);
  if (w !== null) bits.push(w);
  return bits.length === 0 ? "—" : bits.join(" × ");
}

/** How a logged set reads. Same grammar, so the two columns can be compared by eye. */
export function describePerformedSet(s: PerformedSet, unitSystem: string | null, km = false): string {
  const typed = typedSet(s.reps, s.holdSeconds, formatWeight(s.weightKg, unitSystem), s.distanceM, s.rounds, km);
  if (typed !== null) return typed;
  const bits: string[] = [];
  if (s.reps !== null) bits.push(`${s.reps}`);
  if (s.holdSeconds !== null) bits.push(`${s.holdSeconds}s`);
  const w = formatWeight(s.weightKg, unitSystem);
  if (w !== null) bits.push(w);
  return bits.length === 0 ? "—" : bits.join(" × ");
}

